import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskedLine, Reveal } from "../components/Reveal";
import { CTABand, Marquee } from "../components/CTA";
import { BlogList } from "../components/blog/BlogList";
import { IMAGES } from "../data/site";

const CHAPTERS = [
  {
    num: "01",
    title: "Внимание преди всичко",
    text: "Никаква прибързана продажба. Всичко започва с разговор – за вас, вашия ритъм и това, което търсите.",
  },
  {
    num: "02",
    title: "Личен подход",
    text: "Всяка препоръка е индивидуална. Не вярвам в универсални решения – вярвам в хората.",
  },
  {
    num: "03",
    title: "Пълна дискретност",
    text: "Всичко споделено остава между нас. Доверието е основата, върху която работя.",
  },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden" data-testid="hero-section">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" aria-hidden="true" className="pointer-events-none absolute -right-20 top-10 h-[26rem] w-[26rem] rotate-12 select-none text-burgundy opacity-[0.05] lg:h-[34rem] lg:w-[34rem]">
          <path d="M20.5 3.5c-6.5 0-13 4.8-13 11.3 0 3.4 2.6 5.7 5.6 5.7 2.7 0 4.5-1.9 4.5-4.2 0-2-1.5-3.4-3.3-3.4-1.5 0-2.6 1.1-2.6 2.4 0 1.1.8 1.9 1.8 1.9" />
        </svg>
        <div className="mx-auto grid max-w-[1400px] gap-10 px-6 pb-24 pt-16 lg:grid-cols-12 lg:px-12 lg:pb-32 lg:pt-28">
          <motion.div style={{ y: textY }} className="relative z-10 lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="font-sans text-[11px] font-medium normal-case text-burgundy md:uppercase"
              data-testid="hero-eyebrow"
            >
              <span className="block tracking-[0.05em] md:inline md:tracking-[0.35em]">Официален представител на PURTIER</span>
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="mt-2 text-[10px] font-medium uppercase tracking-[0.3em] text-gold md:mt-3 md:tracking-[0.5em]"
              data-testid="hero-tagline"
            >
              Premium &amp; Precious
            </motion.p>
            <h1 className="mt-10 font-serif text-5xl font-light leading-[1.05] text-graphite sm:text-6xl lg:text-[5.2rem]" data-testid="hero-heading">
              <MaskedLine delay={0.15}>Искате да остарявате</MaskedLine>
              <MaskedLine delay={0.3} className="italic text-burgundy">като катедрала?</MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.45 }}
              className="mt-10 max-w-2xl text-base leading-relaxed text-graphite/70"
              data-testid="hero-subtitle"
            >
              <span className="block font-serif text-3xl text-burgundy">Тогава да поговорим.</span>
              <span className="mt-4 block italic">Личен wellbeing план след 40 - според Вашето желание и бюджет.</span>
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-12 flex flex-wrap items-center gap-6"
            >
              <Link
                to="/kontakti"
                data-testid="hero-cta-button"
                className="border border-burgundy bg-transparent px-10 py-4 text-[12px] uppercase tracking-[0.2em] text-burgundy transition-colors duration-[400ms] hover:bg-burgundy hover:text-sand"
              >
                Запазете безплатна консултация
              </Link>
              <Link
                to="/purtier"
                data-testid="hero-secondary-link"
                className="group flex items-center gap-3 text-[12px] uppercase tracking-[0.2em] text-graphite/70 transition-colors duration-500 hover:text-burgundy"
              >
                Запознайте се с PURTIER
                <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-1.5" />
              </Link>
            </motion.div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative lg:col-span-5 lg:mt-24"
            data-testid="hero-image-frame"
          >
            <div className="relative overflow-hidden">
              <motion.img
                style={{ y: imgY }}
                animate={{ scale: [1.05, 1.13, 1.05] }}
                transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }}
                src="/images/hero-silk.webp"
                alt="PURTIER седмо поколение – опаковката върху дълбока бордо коприна"
                className="h-[420px] w-full object-cover lg:h-[560px]"
                data-testid="hero-image"
                fetchpriority="high"
                loading="eager"
              />
            </div>
            <p className="mt-4 text-[11px] uppercase tracking-[0.3em] text-graphite/40">PURTIER седмо поколение – родено в Нова Зеландия</p>
          </motion.div>
        </div>
      </section>

      {/* PORTRAIT – immediately after hero */}
      <section className="border-t border-hairline texture-stone" data-testid="portrait-section">
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 px-6 py-16 lg:grid-cols-12 lg:px-12 lg:py-28">
          <Reveal className="lg:col-span-5 lg:col-start-1">
            <div className="relative">
              <div className="absolute -left-4 -top-4 h-full w-full border border-gold/60" />
              <img
                src="/images/purtier-portrait-luxury-v2.png"
                alt="Биляна Димова – личен консултант PURTIER"
                className="relative h-[480px] w-full bg-[#E9DFD2] object-contain object-center lg:h-[560px]"
                data-testid="portrait-image"
              />
            </div>
          </Reveal>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal>
              <p className="eyebrow" data-testid="portrait-eyebrow">Вашият личен консултант</p>
              <h2 className="mt-8 font-serif text-4xl font-light leading-tight text-graphite sm:text-5xl" data-testid="portrait-name">
                Биляна Димова
              </h2>
              <p className="mt-8 max-w-lg text-base leading-relaxed text-graphite/70">
                Вярвам, че изборът на грижа за себе си заслужава време, внимание и личен разговор. Затова всяка моя
                препоръка започва с консултация – спокойно, дискретно и съобразено изцяло с вас и вашите въпроси.
              </p>
              <blockquote className="mt-10 border-l border-burgundy pl-8 font-serif text-2xl font-light italic leading-snug text-burgundy">
                „Не продавам продукт. Градя доверие <span className="font-sans not-italic">-</span> като катедрала, камък по камък.“
              </blockquote>
              <p className="mt-8 flex items-center gap-3 text-[11px] uppercase tracking-[0.25em] text-gold">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true" className="h-4 w-4">
                  <path d="M20.5 3.5c-6.5 0-13 4.8-13 11.3 0 3.4 2.6 5.7 5.6 5.7 2.7 0 4.5-1.9 4.5-4.2 0-2-1.5-3.4-3.3-3.4-1.5 0-2.6 1.1-2.6 2.4 0 1.1.8 1.9 1.8 1.9" />
                </svg>
                Ранг Team Elite Pavo · RIWAY Europe, Катовице 2026
              </p>
              <Link
                to="/za-men"
                data-testid="portrait-about-link"
                className="group mt-10 inline-flex items-center gap-3 text-[12px] uppercase tracking-[0.2em] text-graphite/70 transition-colors duration-500 hover:text-burgundy"
              >
                Познайте ме по-близко
                <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-1.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee items={["PURTIER · седмо поколение", "Личен подход", "Дискретност", "Внимание към детайла", "Грижа с отношение"]} />

      {/* MANIFESTO CHAPTERS */}
      <section className="mx-auto max-w-[1400px] px-6 py-16 lg:px-12 lg:py-28" data-testid="manifesto-section">
        <Reveal>
          <p className="eyebrow">Моят подход</p>
          <h2 className="mt-8 max-w-2xl font-serif text-4xl font-light leading-tight sm:text-5xl">
            Три принципа, <em className="italic text-burgundy">едно обещание.</em>
          </h2>
        </Reveal>
        <div className="mt-16 grid gap-px bg-hairline md:grid-cols-3">
          {CHAPTERS.map((ch, i) => (
            <Reveal key={ch.num} delay={i * 0.12} className="bg-sand p-10 lg:p-14" data-testid={`chapter-${ch.num}`}>
              <p className="font-serif text-5xl font-light text-gold/80">{ch.num}</p>
              <h3 className="mt-8 font-serif text-2xl font-normal">{ch.title}</h3>
              <p className="mt-5 text-sm leading-relaxed text-graphite/65">{ch.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CAPSULES – перорален прием */}
      <section className="border-t border-hairline" data-testid="home-capsules">
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 px-6 py-16 lg:grid-cols-12 lg:px-12 lg:py-24">
          <Reveal className="lg:col-span-6">
            <div className="overflow-hidden">
              <img
                src={IMAGES.productCapsules}
                alt="Капсулите на PURTIER седмо поколение – макро детайл"
                className="h-[360px] w-full object-cover transition-transform duration-[1600ms] ease-out hover:scale-105 lg:h-[460px]"
                data-testid="home-capsules-image"
              />
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5 lg:col-start-8">
            <p className="eyebrow">Прием на капсули</p>
            <h2 className="mt-6 font-serif text-4xl font-light leading-tight">
              Прием на капсули. <em className="italic text-burgundy">Дискретно, навсякъде.</em>
            </h2>
            <p className="mt-6 text-sm leading-relaxed text-graphite/65">
              PURTIER е под формата на капсули. Ентеричното покритие пази съдържанието
              от стомашната киселина, а аз ще ви запозная с препоръчителната схема на прием по време на консултацията.
            </p>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-t border-hairline" data-testid="testimonials-section">
        <div className="mx-auto max-w-[1400px] px-6 py-16 lg:px-12 lg:py-24">
          <Reveal>
            <p className="eyebrow">Отзиви</p>
            <h2 className="mt-8 max-w-2xl font-serif text-4xl font-light leading-tight sm:text-5xl">
              Думи на хора, <em className="italic text-burgundy">които ми се довериха.</em>
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-px bg-hairline md:grid-cols-3">
            {[
              {
                quote: "Без прибързване и без натиск – първият разговор, при който наистина ме изслушаха.",
                author: "М. К., на 48",
              },
              {
                quote: "Получих честни отговори на всички въпроси, преди да реша каквото и да е. Това е рядкост.",
                author: "В. Д., на 55",
              },
              {
                quote: "Дискретно, топло и професионално. Точно така трябва да изглежда личната консултация.",
                author: "С. И., на 61",
              },
            ].map((t, i) => (
              <Reveal key={i} delay={i * 0.12} className="flex flex-col justify-between bg-sand p-10 lg:p-14" data-testid={`testimonial-${i}`}>
                <p className="font-serif text-2xl font-light italic leading-snug text-graphite">„{t.quote}"</p>
                <p className="mt-8 text-[11px] uppercase tracking-[0.3em] text-burgundy">{t.author}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GLAMOUR MOMENT – full bleed silk */}
      <section className="relative flex min-h-[85vh] items-end overflow-hidden bg-[#180308]" data-testid="glamour-section">
        <motion.img
          src="/images/hero-silk.webp"
          alt="PURTIER седмо поколение върху бордо коприна"
          className="absolute inset-0 h-full w-full object-contain object-[70%_center]"
          animate={{ scale: [1, 1.06, 1] }}
          transition={{ duration: 30, repeat: Infinity, ease: "easeInOut" }}
          data-testid="glamour-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2A0208]/85 via-[#2A0208]/20 to-transparent" />
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 pb-20 lg:px-12 lg:pb-28">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.35em] text-gold">PURTIER · седмо поколение</p>
            <h2 className="mt-8 max-w-3xl font-serif text-4xl font-light leading-[1.1] text-sand sm:text-5xl lg:text-6xl">
              Истинският лукс е <em className="italic text-gold">вниманието.</em>
            </h2>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-sand/75">
              <span className="block">Формула с внимателно подбрани 14 съставки.</span>
              <span className="block">Произведено в Нова Зеландия.</span>
              <span className="block">18 години на глобалния пазар. 6 в Европа.</span>
            </p>
            <Link
              to="/purtier"
              data-testid="teaser-purtier-link"
              className="group mt-10 inline-flex items-center gap-3 text-[12px] uppercase tracking-[0.2em] text-sand/85 transition-colors duration-500 hover:text-gold"
            >
              Научете повече
              <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* БЛОГ */}
      <section className="border-t border-hairline texture-stone" data-testid="home-blog-section">
        <div className="mx-auto max-w-[1400px] px-6 py-14 lg:px-12 lg:py-20">
          <Reveal>
            <p className="eyebrow" data-testid="home-blog-heading">Блог</p>
          </Reveal>
          <BlogList />
        </div>
      </section>

      <CTABand />
    </div>
  );
};

export default Home;
